import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TablePagination,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HistoryIcon from "@mui/icons-material/History";
import Api from "../services/apis/setup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDate, rowsPerPageOptions } from "../utils/helper";
import ResponsiveTable from "../components/ResponsiveTable";
import { useAppSelector } from "../store";
import { getAuthState } from "../store/slices/authSlice";
import VehicleTable from "../components/VehicleTable";

type ServiceHistory = {
  _id: string;
  spareChangedDate: string;
  partDetails: string;
  amountOfParts: number;
  description: string;
  sparePreviousModifiedDate: string;
  nextOdoCheckPoint: number;
  changedBy: string;
  remarks: string;
};

type TransportDetails = {
  registrationNumber: string;
  chasisNumber: number;
  engineNumber: string;
  transportName: string;
  details: {
    owner: string;
    address: string;
    contact: string;
  };
};

type Vehicle = {
  _id: string;
  number: string;
  odo: number;
  lastService: string;
  nextServiceDue: string;
  nextOilChangeOdo: number;
  nextGreasingOdo: number;
  remarks: string;
  transport: TransportDetails;
  history: ServiceHistory[];
};

function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialRowsPerPage = parseInt(searchParams.get('limit') || '5', 10);

  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentVehicle, setCurrentVehicle] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(searchParams.get('date') || null);
  const datePickerRef = useRef<any>(null);
  const navigate = useNavigate();
  const { user } = useAppSelector(getAuthState);
  const [fetchAllVehicles, { isLoading: isFetching, isFetching: isApiFetching }] = Api.useLazyFetchAllVehiclesQuery();
  const [updateVehicleApi] = Api.useUpdateVehicleMutation();
  const [deleteVehicleApi] = Api.useDeleteVehicleMutation();

  // Show loader when API is fetching
  const loading = isFetching || isApiFetching;

  // Helper function to calculate total cost for a vehicle
  const calculateTotalCost = (vehicle: any) => {
    if (!vehicle.history || !Array.isArray(vehicle.history)) return 0;
    return vehicle.history.reduce((total: number, service: any) => {
      return total + (service.amountOfParts || 0);
    }, 0);
  };

  // Get vehicleList from Redux store
  const vehicleList = useSelector(
    (state: RootState) => {
      const list = state.vehicle.vehicleList;
      console.log('[Home] Redux vehicleList:', list?.length || 0, 'vehicles');
      return list;
    }
  );


  const handleEditClick = (vehicle: any) => {
    setCurrentVehicle(vehicle);
    setEditDialogOpen(true);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (vehicleId: string) => {
    setVehicleToDelete(vehicleId);
    setDeleteDialogOpen(true);
    setCountdown(null);
  };

  const startDeleteCountdown = () => {
    setCountdown(3);
    setIsDeleting(true);

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(countdownInterval);
          performDelete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const performDelete = async () => {
    if (!vehicleToDelete) return;

    try {
      await deleteVehicleApi({
        baseURL: `/vehicles/${vehicleToDelete}`,
        method: 'DELETE',
        arguments: { id: vehicleToDelete }
      } as any).unwrap();

      // Show success message
      setCountdown(null);
      setDeleteDialogOpen(false);
      setIsDeleting(false);
      setVehicleToDelete(null);
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setVehicleToDelete(null);
    setCountdown(null);
    setIsDeleting(false);
  };

  const handleEditSubmit = async () => {
    if (currentVehicle) {
      try {
        await updateVehicleApi({
          baseURL: `/vehicles/${currentVehicle._id}`,
          method: 'PUT',
          payload: JSON.stringify(currentVehicle),
          arguments: { id: currentVehicle._id, data: currentVehicle }
        }).unwrap();
        setEditDialogOpen(false);
        setCurrentVehicle(null);
      } catch (error) {
        console.error("Error updating vehicle:", error);
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params, { replace: true });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    params.set('limit', String(newRowsPerPage));
    params.set('page', '0');
    setSearchParams(params, { replace: true });
  };

  const handleDateChange = (date: Date | null) => {
    const dateStr = date ? date.toISOString().split('T')[0] : null;
    setSelectedDate(dateStr);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (dateStr) params.set('date', dateStr); else params.delete('date');
    params.set('page', '0');
    setSearchParams(params, { replace: true });
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    params.delete('date');
    params.set('page', '0');
    setSearchParams(params, { replace: true });
  };

  const fetchVehicles = async () => {
    console.log('[Home] Fetching vehicles with page:', page + 1, 'limit:', rowsPerPage, 'date:', selectedDate);
    const params: any = {
      page: page + 1,
      limit: rowsPerPage
    };
    if (selectedDate) params.date = selectedDate;

    console.log('[Home] API params:', params);

    const result: any = await fetchAllVehicles({
      baseURL: "/vehicles",
      params
    });

    // Debug: Log the raw result structure
    console.log('[Home] Raw result:', result);

    // RTK Query wraps response - need to handle different wrapping levels
    // Backend: { data: [...], pagination: {...} }
    // RTK Query wraps: { data: { data: [...], pagination: {...} } }
    let response = result?.data;
    if (!response) {
      response = result;
    }

    console.log('[Home] Processed response:', response);

    // Handle pagination - extract vehicles and pagination info
    // The vehicles could be at response.data or response.data.data
    let vehiclesArray: any[] = [];
    let paginationData: any = null;

    if (Array.isArray(response)) {
      // Direct array response (legacy)
      vehiclesArray = response;
    } else if (response?.data) {
      if (Array.isArray(response.data)) {
        // response.data is the vehicles array
        vehiclesArray = response.data;
        paginationData = response.pagination;
      } else if (response.data.data) {
        // response.data.data is the vehicles array (double-wrapped)
        vehiclesArray = response.data.data;
        paginationData = response.data.pagination;
      }
    }

    console.log('[Home] Vehicles array:', vehiclesArray.length);
    console.log('[Home] Pagination data:', paginationData);

    // Set total vehicles
    if (paginationData?.total !== undefined) {
      console.log('[Home] Setting totalVehicles from pagination:', paginationData.total);
      setTotalVehicles(paginationData.total);
    } else {
      // Fallback - use array length
      console.log('[Home] Setting totalVehicles from array length:', vehiclesArray.length);
      setTotalVehicles(vehiclesArray.length);
    }
  };

  const handleRefresh = async () => {
    await fetchVehicles();
  };

  useEffect(() => {
    console.log('[Home] useEffect triggered:', { page, rowsPerPage, selectedDate, hasUser: !!user });
    if (user) {
      fetchVehicles();
    }
  }, [page, rowsPerPage, selectedDate, user]);

  const handleServiceHistory = (vehicleId: string) => {
    return navigate(`/service-history/${vehicleId}`);
  }
  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "space-between" },
          alignItems: "center",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" gutterBottom>
          Vehicle Maintenance Overview
        </Typography>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* <DatePicker
              value={selectedDate ? new Date(selectedDate) : null}
              onChange={handleDateChange}
              label="Filter by Date"
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  placeholder: 'Select date',
                  sx: {
                    width: { xs: 140, sm: 180 },
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      color: 'var(--primary-color)',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--primary-color)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--primary-color)',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'var(--primary-color)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'var(--primary-color)',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'var(--primary-color)',
                    },
                  },
                },
              }}
            /> */}
          </LocalizationProvider>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => {
              clearDateFilter();
              handleRefresh();
            }}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'var(--primary-dark)',
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <VehicleTable
            vehicles={vehicleList}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onViewHistory={handleServiceHistory}
          />
        </Box>
      )}
      <TablePagination
        component="div"
        count={totalVehicles}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Vehicle Information
          </Typography>
        </DialogTitle>
        <DialogContent>
          {currentVehicle && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={3}>
                {/* Vehicle Identification Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    Vehicle Identification
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Vehicle Registration Number"
                    value={currentVehicle.number}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, number: e.target.value})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Chassis Number"
                    value={currentVehicle.transport?.chasisNumber || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, transport: {...currentVehicle.transport, chasisNumber: e.target.value}})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Engine Number"
                    value={currentVehicle.transport?.engineNumber || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, transport: {...currentVehicle.transport, engineNumber: e.target.value}})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Transport Information Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, mt: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    Transport Information
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Transport Name"
                    value={currentVehicle.transport?.transportName || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, transport: {...currentVehicle.transport, transportName: e.target.value}})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Owner"
                    value={currentVehicle.transport?.details?.owner || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, transport: {...currentVehicle.transport, details: {...currentVehicle.transport?.details, owner: e.target.value}}})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Address"
                    value={currentVehicle.transport?.details?.address || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, transport: {...currentVehicle.transport, details: {...currentVehicle.transport?.details, address: e.target.value}}})}
                    multiline
                    rows={2}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Contact"
                    value={currentVehicle.transport?.details?.contact || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, transport: {...currentVehicle.transport, details: {...currentVehicle.transport?.details, contact: e.target.value}}})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Service Information Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, mt: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    Service Information
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Current ODO Reading"
                    type="number"
                    value={currentVehicle.odo || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, odo: parseInt(e.target.value) || 0})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Last Service Date"
                    value={currentVehicle.lastService ? new Date(currentVehicle.lastService) : null}
                    onChange={(date) => setCurrentVehicle({...currentVehicle, lastService: date ? date.toISOString().split('T')[0] : ''})}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                              borderWidth: 1,
                            },
                          },
                          '& .MuiInputBase-input': {
                            '&:focus': {
                              outline: 'none',
                            },
                          },
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Next Service Due"
                    value={currentVehicle.nextServiceDue ? new Date(currentVehicle.nextServiceDue) : null}
                    onChange={(date) => setCurrentVehicle({...currentVehicle, nextServiceDue: date ? date.toISOString().split('T')[0] : ''})}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                              borderWidth: 1,
                            },
                          },
                          '& .MuiInputBase-input': {
                            '&:focus': {
                              outline: 'none',
                            },
                          },
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Next Oil Change ODO"
                    type="number"
                    value={currentVehicle.nextOilChangeOdo || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, nextOilChangeOdo: parseInt(e.target.value) || 0})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Next Greasing ODO"
                    type="number"
                    value={currentVehicle.nextGreasingOdo || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, nextGreasingOdo: parseInt(e.target.value) || 0})}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Remarks Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, mt: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    Additional Information
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Remarks"
                    value={currentVehicle.remarks || ''}
                    onChange={(e) => setCurrentVehicle({...currentVehicle, remarks: e.target.value})}
                    multiline
                    rows={3}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                          borderWidth: 1,
                        },
                      },
                      '& .MuiInputBase-input': {
                        '&:focus': {
                          outline: 'none',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        </LocalizationProvider>
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#f5f5f5' }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#e0e0e0',
              color: '#666',
              '&:hover': {
                borderColor: '#bdbdbd',
                backgroundColor: '#fafafa'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: 'var(--primary-color)',
              '&:hover': {
                backgroundColor: 'var(--primary-dark)'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {countdown !== null ? "Deleting Vehicle..." : "Confirm Vehicle Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {countdown !== null ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {countdown > 0 ? countdown : "Deleting..."}
                </Typography>
                <Typography variant="body1">
                  {countdown > 0
                    ? "Vehicle will be deleted in..."
                    : "Deleting vehicle, please wait..."}
                </Typography>
                {isDeleting && countdown <= 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            ) : (
              "Are you sure you want to delete this vehicle? This action cannot be undone and all associated service history will be permanently removed."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {countdown === null && (
            <>
              <Button onClick={cancelDelete} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                onClick={startDeleteCountdown}
                color="error"
                variant="contained"
                disabled={isDeleting}
              >
                Delete
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Home;